import cn from "classnames"
import { throttle } from "lodash-es"
import React, { useState, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { connect, useDispatch } from "react-redux"
import Sign from "@/signomatic/app/sign"
import RowButton from "@/signomatic/components/row_button"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"
import { EditOutline, DuplicateOutline, TrashOutline, HorizontalCenter, VerticalCenter } from "@/skylticons"

interface ControlBarProps {
  sign: Sign
  activeObject: SignObjectState | null
  toolbox: string | null
}

const ControlBar: React.FC<ControlBarProps> = ({ sign, activeObject, toolbox }) => {
  const { t } = useTranslation()
  const [objectPosition, setObjectPosition] = useState<number[]>([])
  const dispatch = useDispatch()

  const positionChange = useMemo(
    () =>
      throttle((position: Array<number>) => {
        setObjectPosition(position)
      }, 16),
    [setObjectPosition]
  )

  useEffect(() => {
    sign?.onObjectPositionChanged.subscribe(positionChange)

    return () => {
      sign?.onObjectPositionChanged.unsubscribe(positionChange)
    }
  }, [sign, positionChange])

  if (!activeObject) {
    return null
  }

  if (!objectPosition[0] || !objectPosition[1]) {
    return null
  }

  const canvasActiveObject = sign.canvas.getActiveObject()
  const highestY =
    canvasActiveObject && canvasActiveObject.oCoords
      ? Math.min(
          canvasActiveObject.oCoords.bl.y,
          canvasActiveObject.oCoords.br.y,
          canvasActiveObject.oCoords.tl.y,
          canvasActiveObject.oCoords.tr.y
        )
      : 0
  const leftPosition = Math.max(Math.min(objectPosition[1], sign?.canvas?.getWidth() - 150), 150)
  const topPosition = Math.max(highestY - 82 - (activeObject.angle > 110 && activeObject.angle < 250 ? 60 : 0), 4)

  return (
    <div
      className={cn("control-bar-wrapper", {
        hidden: ["image", "text"].includes(toolbox || "") || !sign.activeObject.hasControls,
      })}
    >
      {activeObject && (
        <div
          className="absolute z-20 flex items-center rounded border-1 bg-white p-1 shadow-md"
          style={{
            top: `${topPosition}px`,
            left: `${leftPosition}px`,
            transform: "translate(-50%, 0%)",
          }}
        >
          <span className="flex items-center">
            <button
              className="button button-subtle no-hover-mobile no-focus mb-0"
              onClick={() => dispatch(actions.setToolbox(Sign.isText(activeObject) ? "text" : "image"))}
              type="button"
            >
              <EditOutline />
              {t("misc.edit")}
            </button>

            {sign.signType !== "contour_cut" && <div className="mx-1 my-0 h-6 w-px bg-white-20 p-0"></div>}
          </span>
          {sign.signType !== "contour_cut" && (
            <span className="flex items-center">
              <RowButton
                className="button button-subtle icon-only no-hover-mobile no-focus mb-0 ml-0"
                iconButton
                tooltip={t("toolbox.center_horizontal")}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  sign.updateObjectState({ left: 0 })
                }}
              >
                <HorizontalCenter />
              </RowButton>
              <RowButton
                className="button button-subtle icon-only no-hover-mobile no-focus mb-0 ml-0"
                iconButton
                tooltip={t("toolbox.center_vertical")}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  sign.updateObjectState({ top: 0 })
                }}
              >
                <VerticalCenter />
              </RowButton>
              <RowButton
                className="button button-subtle icon-only no-hover-mobile no-focus mb-0 ml-0"
                iconButton
                tooltip={t("misc.duplicate")}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()

                  if (activeObject) {
                    sign.duplicateObject(activeObject, sign.centerX, sign.centerY)
                  }
                }}
              >
                <DuplicateOutline />
              </RowButton>
              <div className="mx-1 my-0 h-6 w-px bg-white-20 p-0"></div>
              <RowButton
                className="button button-subtle button-error icon-only no-hover-mobile no-focus mb-0 ml-0"
                iconButton
                tooltip={t("misc.remove")}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()

                  sign.deleteObject(activeObject)
                }}
              >
                <TrashOutline />
              </RowButton>
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default connect(({ activeObject, toolbox }: State) => ({ activeObject, toolbox }))(ControlBar)
